import React, { useCallback, useRef, useState } from "react";

/**
 * Stepped range slider — a plain rail, a round handle, and a bounce as the
 * handle lands on each step.
 *
 * Two skins, because the two sliders on the order ticket are not doing the
 * same job:
 *
 *   "plain"  — hairline rail, round handle. For the deposit share, which has
 *              its own labelled stops printed under it and its value stated in
 *              words above it; a trough there is 34px of chrome around a
 *              number already written out twice.
 *   "trough" — capsule track with a dot per tick and a pill thumb seated
 *              inside it. For leverage, where the scale runs 1-100 with no
 *              labels, so the dots are the only thing saying how far along the
 *              range the handle is.
 *
 * A port of the beui `range-slider` behaviour rather than the package: that
 * one ships as a shadcn registry entry (TSX, `motion`), and this app is CRA
 * JavaScript with neither installed. The parts that matter are the interaction
 * and the motion, and both are a few lines without a runtime.
 *
 * The bounce is not decoration. A stepped slider snaps, and without a landing
 * cue a snap is indistinguishable from the pointer having moved — the kick is
 * what says the value has committed to a detent. It is keyed off the step
 * index, so it replays per detent crossed rather than per pointer event.
 *
 * @param {object} props
 * @param {number} props.value
 * @param {(value: number) => void} props.onChange - Fired with a snapped value
 * @param {number} [props.min=0]
 * @param {number} [props.max=100]
 * @param {number} [props.step=5]
 * @param {boolean} [props.disabled]
 * @param {string} [props.color] - Fill and thumb colour
 * @param {(value: number) => string} [props.format] - Screen-reader value text
 * @param {"plain"|"trough"} [props.variant="plain"]
 * @param {number} [props.tickEvery] - Trough skin: draw a dot every N units
 * @param {string} [props.ariaLabel]
 */
/** Hit area around the 4px rail — the rail is the drawing, not the target. */
const ROW_H = 22;
/** Trough height: enough to hold the dot row and a full-height thumb. */
const TROUGH_H = 30;
/** Half the handle, so neither end of the scale draws it past the rail. */
const EDGE = 11;
/** `left` for a 0-100 position, inset from both ends by `EDGE`. */
const seat = (p) => `calc(${p}% + ${((50 - p) / 50) * EDGE}px)`;

const RangeSlider = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 5,
  disabled = false,
  color = "var(--cat-accent, #5B1DEE)",
  format,
  variant = "plain",
  tickEvery,
  ariaLabel = "Value",
}) => {
  const trough = variant === "trough";
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const span = Math.max(1, max - min);
  const clamped = Math.min(max, Math.max(min, Number(value) || min));
  const pct = ((clamped - min) / span) * 100;
  const stepIndex = Math.round((clamped - min) / step);

  // Dots only in the trough skin, and never one per step: leverage runs to
  // 100 in 1× detents, which would draw a solid line of them.
  const dots = [];
  if (trough) {
    const every = tickEvery || step;
    for (let v = min; v <= max + 1e-9; v += every) dots.push(v);
  }

  const snap = useCallback(
    (clientX) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const raw = min + ratio * span;
      const snapped = Math.min(max, Math.max(min, Math.round(raw / step) * step));
      // Float steps land on values like 24.999999999; the value is only ever
      // read back at the step's own precision.
      const fixed = Number(snapped.toFixed(6));
      if (fixed !== clamped) onChange?.(fixed);
    },
    [clamped, max, min, onChange, span, step]
  );

  const onPointerDown = (e) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    snap(e.clientX);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    const big = step * 5;
    const next = {
      ArrowLeft: clamped - step,
      ArrowDown: clamped - step,
      ArrowRight: clamped + step,
      ArrowUp: clamped + step,
      PageDown: clamped - big,
      PageUp: clamped + big,
      Home: min,
      End: max,
    }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    onChange?.(Number(Math.min(max, Math.max(min, next)).toFixed(6)));
  };

  return (
    <div
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={clamped}
      aria-valuetext={format ? format(clamped) : undefined}
      aria-disabled={disabled || undefined}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={(e) => dragging && snap(e.clientX)}
      onPointerUp={() => setDragging(false)}
      onPointerCancel={() => setDragging(false)}
      className="ce-rs"
      style={{
        position: "relative",
        height: trough ? TROUGH_H : ROW_H,
        display: "flex",
        alignItems: "center",
        touchAction: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        outline: "none",
      }}
    >
      <style>
        {`
          @keyframes ceRsLand {
            0%   { transform: translate(-50%, -50%) scale(1); }
            40%  { transform: translate(-50%, -50%) scale(1.16); }
            100% { transform: translate(-50%, -50%) scale(1); }
          }
          .ce-rs:focus-visible .ce-rs-track {
            box-shadow: var(--inset), 0 0 0 3px var(--cat-accent-soft, rgba(91, 29, 238, .22));
          }
          @media (prefers-reduced-motion: reduce) {
            .ce-rs-thumb { animation: none !important; }
            .ce-rs-fill, .ce-rs-thumb { transition: none !important; }
          }
        `}
      </style>

      <div
        ref={trackRef}
        className="ce-rs-track"
        style={{
          position: "relative",
          width: "100%",
          // Anything you drag along is recessed — the same rule as an input.
          ...(trough
            ? {
                height: TROUGH_H,
                borderRadius: 999,
                background: "var(--surface-2)",
                boxShadow: "var(--inset)",
                border: 0,
                overflow: "hidden",
              }
            : {
                height: 6,
                borderRadius: 999,
                background: "var(--surface-2)",
                boxShadow: "var(--inset-sm)",
              }),
        }}
      >
        <div
          className="ce-rs-fill"
          style={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            // Ends under the handle's centre, not at the raw percentage, so
            // the fill and the handle agree at both ends of the scale.
            width: seat(pct),
            borderRadius: 999,
            // At trough height a solid fill reads as a second surface sitting
            // on the track and swallows the dots; the hairline rail needs the
            // solid colour to register at all.
            background: trough
              ? `linear-gradient(90deg, color-mix(in srgb, ${color} 14%, transparent), color-mix(in srgb, ${color} 30%, transparent))`
              : color,
            transition: dragging ? "none" : "width .18s cubic-bezier(.22,1,.36,1)",
          }}
        />

        {dots.map((v) => {
          const p = ((v - min) / span) * 100;
          const passed = v <= clamped + 1e-9;
          return (
            <span
              key={v}
              aria-hidden="true"
              style={{
                position: "absolute",
                left: seat(p),
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: 3.5, height: 3.5, borderRadius: "50%",
                background: passed ? color : "var(--ink-4, #98A2B3)",
                opacity: passed ? 0.5 : 0.55,
              }}
            />
          );
        })}

        {/* Trough: a pill thumb, because a vertical edge reads as a position
            on a scale where a circle floating over one reads as a handle. */}
        <span
          className="ce-rs-thumb"
          key={stepIndex}
          style={{
            position: "absolute",
            left: seat(pct),
            top: "50%",
            transform: "translate(-50%, -50%)",
            // A circle in both skins. The trough's thumb used to be a 6px
            // vertical bar, which reads as a *tick* — one of the dots behind
            // it, drawn taller — rather than as the thing you grab.
            ...(trough
              ? {
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: `linear-gradient(180deg, ${color}, color-mix(in srgb, ${color} 82%, transparent))`,
                  boxShadow: "0 5px 12px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.35)",
                }
              : {
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: "var(--raise)",
                  border: `3px solid ${color}`,
                  boxShadow: "var(--pillow-sm)",
                }),
            animation: "ceRsLand .16s ease-out",
            transition: dragging ? "none" : "left .18s cubic-bezier(.22,1,.36,1)",
          }}
        />
      </div>
    </div>
  );
};

export default RangeSlider;
